import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import { keyframes } from '@mui/material/styles';
import { useTemplates } from '../data/templates';

import TemplateSelector from '../components/TemplateSelector';
import PromotionForm from '../components/PromotionForm';
import ImportPromotionsDialog from '../components/ImportPromotionsDialog';
import { PageTitle } from '../../../../../_metronic/layout/core';
import type { FormValues } from '../types';

interface ReusePromotionState {
  reuseBriefing?: boolean;
  sourceBriefingId?: string | number;
  templateId?: string;
  values?: FormValues;
}

const enterTransition = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export default function CreatePromotionPage() {
  const templates = useTemplates();
  const location = useLocation();
  const routeReuseState = location.state as ReusePromotionState | null;
  const [reuseData, setReuseData] = useState<ReusePromotionState | null>(() => {
    if (
      !routeReuseState?.reuseBriefing ||
      !routeReuseState.templateId ||
      !routeReuseState.values ||
      !templates.some((template) => template.id === routeReuseState.templateId)
    ) {
      return null;
    }

    return routeReuseState;
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(() => reuseData?.templateId ?? null);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) ?? null;
  const shouldUseReuseData = Boolean(reuseData && selectedTemplateId === reuseData.templateId);

  const handleSelectTemplate = (id: string) => {
    if (id !== reuseData?.templateId) {
      setReuseData(null);
    }

    setSelectedTemplateId(id);
  };

  const handleResetTemplate = () => {
    setReuseData(null);
    setSelectedTemplateId(null);
  };

  return (
    <div className="h-[calc(100vh-150px)] bg-gray-50">
      <PageTitle>Briefing de Promoções</PageTitle>
      <div className="mx-auto">
        <div className="mb-4 flex justify-end">
          <ImportPromotionsDialog />
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm animate-fade-in md:p-8">
          <Box
            key={selectedTemplate?.id ?? 'template-selector'}
            sx={{
              animation: `${enterTransition} 0.35s ease-out`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            {!selectedTemplate ? (
              <TemplateSelector
                templates={templates}
                selectedId={selectedTemplateId}
                onSelect={handleSelectTemplate}
              />
            ) : (
              <PromotionForm
                template={selectedTemplate}
                onReset={handleResetTemplate}
                initialValues={shouldUseReuseData ? reuseData?.values : undefined}
                reuseSourceId={shouldUseReuseData ? reuseData?.sourceBriefingId : undefined}
              />
            )}
          </Box>
        </div>
      </div>
    </div>
  );
}
